"use client";

import { create } from "zustand";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api-fetch";

/**
 * 换脸后台任务队列（全局，跨对话框/页面存活）。
 *
 * 🔴 2026-09-29（老板指令）：换脸进行中要支持关闭对话框切到后台处理，
 * 多任务排队，完成后系统提示即可——GPU 出一张图要 2-4 分钟，不能让用户干等。
 *
 * 任务就是一次 fire-and-forget 的 POST /generate（character_faceswap），
 * 状态落在这个 store 里；完成/失败用 sonner toast 系统通知，
 * 悬浮托盘（SwapTaskTray）负责队列可视化与管理（清除已完成/单条移除）。
 */
export interface SwapTask {
  id: string;
  characterName: string;
  /** 用的哪张脸（面孔库 label，如 WH073 / 裁切脸） */
  faceLabel: string;
  status: "running" | "done" | "error";
  startedAt: number;
  finishedAt?: number;
  /** 成功时：同步过期的镜头参考图数量 */
  staleShots?: number;
  error?: string;
}

export interface StartSwapParams {
  projectId: string;
  characterId: string;
  characterName: string;
  faceLabel: string;
  /** 完整的 POST body（action/payload/modelConfig） */
  requestBody: unknown;
  /** 成功后刷新卡片数据（一般传 CharacterCard 的 onUpdate） */
  onSuccess?: () => void;
}

interface SwapTasksState {
  tasks: SwapTask[];
  start: (p: StartSwapParams) => void;
  /** 移除单条（running 也可以移除，只是不再显示；请求本身无法中断） */
  dismiss: (id: string) => void;
  clearFinished: () => void;
}

export const useSwapTasks = create<SwapTasksState>((set) => ({
  tasks: [],

  start: (p) => {
    const tid = `swap_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    set((s) => ({
      tasks: [
        {
          id: tid,
          characterName: p.characterName,
          faceLabel: p.faceLabel,
          status: "running",
          startedAt: Date.now(),
        },
        ...s.tasks,
      ],
    }));
    toast.info(`「${p.characterName}」换脸已转入后台处理，完成后会通知你`, {
      description: "可以继续做别的事，任务进度在右下角托盘。",
    });

    void (async () => {
      try {
        const response = await apiFetch(`/api/projects/${p.projectId}/generate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(p.requestBody),
        });
        const data = (await response.json()) as {
          status?: string;
          error?: string;
          staleShots?: number;
        };
        if (data.status !== "ok") {
          throw new Error(data.error || "换脸失败");
        }
        set((s) => ({
          tasks: s.tasks.map((t) =>
            t.id === tid
              ? { ...t, status: "done", finishedAt: Date.now(), staleShots: data.staleShots }
              : t
          ),
        }));
        toast.success(
          `「${p.characterName}」换脸完成${data.staleShots ? `，${data.staleShots} 个镜头参考图已同步过期` : ""}`
        );
        p.onSuccess?.();
      } catch (err: unknown) {
        const msg =
          err instanceof Error ? err.message.replace(/^(?:HTTP )?\d+\s*/g, "") : String(err);
        set((s) => ({
          tasks: s.tasks.map((t) =>
            t.id === tid ? { ...t, status: "error", finishedAt: Date.now(), error: msg } : t
          ),
        }));
        toast.error(`「${p.characterName}」换脸失败：${msg || "未知错误"}`);
      }
    })();
  },

  dismiss: (id) => set((s) => ({ tasks: s.tasks.filter((t) => t.id !== id) })),

  clearFinished: () =>
    set((s) => ({ tasks: s.tasks.filter((t) => t.status === "running") })),
}));
